import React, { useEffect, useState} from 'react'
import { connect } from 'react-redux'
import { format } from 'date-fns'
import { useHistory, useRouteMatch } from 'react-router-dom'

import UploadedDocumentsTable from './UploadedDocumentsTable'
import LgyField from 'app/components/util/lgy-field/LgyField'
import LgyLoadingIcon from 'app/components/util/lgy-loading-icon/LgyLoadingIcon'
import { LgySection, LgySubsection } from 'app/components/util/lgy-section/LgySection'
import { formatPhone } from 'app/utils/fieldUtils'
import { getAuditType, getAuditUnit, getAuditLocation } from '../util/oversightUtils'
import { getOneAudit } from '../actions/searchAuditsAction'
import { getCorrespondenceList } from '../actions/correspondenceActions'
import OversightDocumentUpload from './OversightDocumentUpload'
import LgyPagination from 'app/components/util/lgy-pagination/LgyPagination'
import 'app/components/util/lgy-pagination/lgy-pagination.scss'

/**
 * 
 * @param {function} [getCorrespondenceList] function for getting full list of correspondence documents
 * @param {object} [auditSearch] the stats of the audit search
 * @param {object} audit the current audit
 * @param {Object[]} [correspondence] list of all the correspondence documents
 * @param {boolean} [canEditAudit] determines if user is one allowed to edit audit from permissions
 * @returns The Audit for the audit container
 */


const Audit = ({ getOneAudit, getCorrespondenceList, auditSearch, audit, correspondence, canEditAudit}) => {

  const {
    params: { auditId }
  } = useRouteMatch('/lgyhub/oversight/audits/:auditId')

  const calculateInitialPage = (changedIndex) => {
    //list is zero based, so first index is zero and we need to add 1.
    let nonZeroBasedIndex = changedIndex + 1
    let initialPage = 1
    //if there's more than 10 items, there will be more than one page
    if(nonZeroBasedIndex > 10) {
      //Need to add 1 so we get the right pge
      //For example, if nonZeroBasedIndex = 9, floor(9 / 10) = 0 + 1 for page 1, or if nonZeroBasaedIndex = 35, floor(35/10) = 3 + 1 for page 4
      initialPage = Math.floor(nonZeroBasedIndex / 10) + 1
      //This is for cases of being on multiples of 10. 1 was added above, but need to subtract for multiples of 10
      //If the item is index 10, 10 % 10 = 0. Since 10 / 10 = 1, the above puts it on page 2, which is not what we want with 10 items per page.
      if(nonZeroBasedIndex % 10 === 0) {
        initialPage = initialPage - 1
      }
    }
    return initialPage
  }

  const history = useHistory()

  const [pageOfItems, setPages] = useState([])

  useEffect(() => {
    getOneAudit && getOneAudit(auditId)
    getCorrespondenceList && getCorrespondenceList({auditId: auditId, correspondenceId: null})
  }, [getOneAudit, getCorrespondenceList, auditId])

  if (auditSearch.fetching) {
    return <LgyLoadingIcon className='read-only-audit' />
  } else if (auditSearch.error) {
    return <div>Audit not found</div>
  } else if (audit) {
    return <LgySection className='read-only-audit' header={
      <span>
        Audit File&nbsp;
        {canEditAudit &&
          <button className='usa-button' onClick={() => history.push(`/lgyhub/oversight/audits/${auditId}/edit`)}>Edit</button>}
      </span>}>
      <LgySubsection className='audit-information' header='Audit Information' />
      <LgyField label='Auditing Unit' value={getAuditUnit(audit.auditingUnitCode)} />
      <LgyField label='Affiliate Name' value={audit.organizationName} />
      <LgyField label='Fiscal Year' value={audit.auditFiscalYear} />
      <LgyField label='Audit Type' value={getAuditType(audit.auditTypeCode)} />
      <LgyField label='Lender/Servicer ID' value={audit.organizationId} />
      <LgyField label='Audit Location' value={getAuditLocation(audit.auditLocationCode)} />
      {audit.auditingUnitCode === 'M' && <LgyField label='Audit Number' value={audit.fullAuditNumber} />}

      <LgySubsection className='audit-dates' header='Audit Dates' />
      <LgyField label='Notification Date' value={audit.notificationDate ? format(audit.notificationDate, 'MM/dd/yyyy', { awareOfUnicodeTokens: true }) : ''} />
      <LgyField label='Close-Out Date' value={audit.closeOutDate ? format(audit.closeOutDate, 'MM/dd/yyyy', { awareOfUnicodeTokens: true }) : ''} />

      <LgySubsection className='va-poc' header='VA Audit Point of Contact' />
      <LgyField label='Name' inlineChildren>
        {audit.vaPocFirstName} {audit.vaPocMiddleInitial ? `${audit.vaPocMiddleInitial} ` : ''} {audit.vaPocLastName}
      </LgyField>
      <LgyField label='Email' value={audit.vaPocEmail} />
      <LgyField label='Primary Phone Number' value={formatPhone(audit.vaPocPrimaryPhone)} />
      <LgyField label='Secondary Phone Number' value={formatPhone(audit.vaPocSecondaryPhone)} />

      <LgySubsection className='external-poc' header='Lender/Servicer Point of Contact' />
      <LgyField label='Name' inlineChildren>
        {audit.orgPocFirstName} {audit.orgPocMiddleInitial ? `${audit.orgPocMiddleInitial} ` : ''} {audit.orgPocLastName}
      </LgyField>
      <LgyField label='Email' value={audit.orgPocEmail} />
      <LgyField label='Address Line One' value={audit.orgPocAddressOne} />
      <LgyField label='Address Line Two' value={audit.orgPocAddressTwo} />
      <LgyField label='City' value={audit.orgPocCity} />
      <LgyField label='State' value={audit.orgPocStateCode} />
      <LgyField label='Zip' value={audit.orgPocZip} />
      <LgyField label='Primary Phone Number' value={formatPhone(audit.orgPocPrimaryPhone)} />
      <LgyField label='Secondary Phone Number' value={formatPhone(audit.orgPocSecondaryPhone)} />

      <LgySubsection className='document-history' header='Document History' />
      <UploadedDocumentsTable auditId={audit.id} auditUnit={audit.auditingUnitCode} documents={pageOfItems} fullDocumentsListLength={correspondence.list ? correspondence.list.length : 0}/>  
      
      {correspondence.list && correspondence.list.length > 0 && <div className='lgy-pagination usa-unstyled-list'><LgyPagination items={correspondence.list} onPageChange={(pageOfItems) => setPages(pageOfItems)} initialPage={calculateInitialPage(correspondence?.changedIndex)} /></div>}
      
      <LgySubsection className='upload-documents' header='Upload Documents' />
      <OversightDocumentUpload auditId={audit.id} auditUnit={audit.auditingUnitCode} />
    </LgySection>
  } else return null
}

const mapStateToProps = state => ({
  auditSearch: state.oversight.auditSearch,
  audit: state.oversight.auditSearch.audit ? state.oversight.auditSearch.audit : undefined,
  correspondence: state.oversight.correspondence
})

const mapDispatchToProps = { getOneAudit, getCorrespondenceList }

export default connect(mapStateToProps, mapDispatchToProps)(Audit)

